import { isOneOf, readVersioned, writeVersioned } from '@/lib/storage/versioned-storage';
import { createContext, useContext, useEffect, useState } from 'react';
import { updateThemeColors } from './theme-utils';

const THEMES = ['light', 'dark'] as const;
type Theme = (typeof THEMES)[number];

const isTheme = isOneOf({ allowed: THEMES });

type ThemeProviderProps = {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
	initialThemeColor?: string;
};

type ThemeProviderState = {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	overrideTheme: (theme: Theme | null) => void;
};

const initialState: ThemeProviderState = {
	theme: 'light',
	setTheme: () => null,
	overrideTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
	children,
	defaultTheme = 'light',
	storageKey = 'vadio-ui-theme',
	initialThemeColor,
	...props
}: ThemeProviderProps) {
	const [currentTheme, setCurrentTheme] = useState<Theme>(
		() => readVersioned({ key: storageKey, isValid: isTheme }) ?? defaultTheme,
	);
	const [overrideTheme, setOverrideTheme] = useState<Theme | null>(null);

	useEffect(() => {
		const root = window.document.documentElement;
		root.classList.remove('light', 'dark');
		root.classList.add(overrideTheme || currentTheme);

		// Initialize theme color if provided
		if (initialThemeColor) {
			updateThemeColors(initialThemeColor);
		}
	}, [currentTheme, overrideTheme, initialThemeColor]);

	const value = {
		theme: overrideTheme || currentTheme,
		setTheme: (theme: Theme) => {
			writeVersioned({ key: storageKey, data: theme });
			setCurrentTheme(theme);
		},
		overrideTheme: (theme: Theme | null) => {
			setOverrideTheme(theme);
		},
	};

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useAppTheme = () => {
	const context = useContext(ThemeProviderContext);

	if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

	return context;
};
